import React, { useState, useEffect } from "react";
import { X, Package, Calendar, User, FileText, AlertCircle } from "lucide-react";
import { useAuth } from "../../context/Auth";
import { showApiErrors } from "../../utils/helpers";
import RoleBasedComponent from "../../components/RoleBasedComponent";

const RequisitionItemDetailsModal = ({ modalOpen, setModalOpen, itemId, onApprove, onReject }) => {
  const { requestWithAuth } = useAuth();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (modalOpen && itemId) {
      fetchDetails();
    }
  }, [modalOpen, itemId]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/requisition/branch/${itemId}/item`,
        "GET"
      );
      setDetails(data);
    } catch (error) {
      showApiErrors(error);
      setModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  if (!modalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
              <Package size={20} />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              Item Details
            </h2>
          </div>
          <button
            onClick={() => setModalOpen(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-500">Loading details...</p>
            </div>
          ) : details ? (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <p className="text-sm text-gray-500 mb-1">Drug</p>
                  <p className="font-semibold text-gray-900 text-lg">{details.drug}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Quantity Requested</p>
                  <p className="font-medium text-gray-900">{details.quantity}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Quantity Supplied</p>
                  <p className="font-medium text-gray-900">{details.quantity_supplied || "N/A"}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  <p className="font-medium text-gray-900">{details.status}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Branch</p>
                  <p className="font-medium text-gray-900">{details.branch}</p>
                </div>
              </div>

              {/* Batches */}
              {details.batch_allocations && details.batch_allocations.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Batch Allocations</h3>
                  <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-100 text-gray-600">
                        <tr>
                          <th className="px-4 py-2 font-medium">Batch Number</th>
                          <th className="px-4 py-2 font-medium">Expiry Date</th>
                          <th className="px-4 py-2 font-medium text-right">Allocated Qty</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {details.batch_allocations.map((batch, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 font-medium text-gray-900">{batch.batch_number}</td>
                            <td className="px-4 py-2 text-gray-600">{batch.expiry_date || "N/A"}</td>
                            <td className="px-4 py-2 text-right font-medium text-gray-900">{batch.allocated_quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Dates & People */}
              <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1 flex items-center gap-1"><Calendar size={14} /> Date Requested</p>
                  <p className="font-medium text-gray-900 text-sm">{details.created_at ? new Date(details.created_at).toLocaleString() : "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1 flex items-center gap-1"><User size={14} /> Requested By</p>
                  <p className="font-medium text-gray-900 text-sm">{details.requested_by_name || "N/A"}</p>
                </div>

                {details.date_approved && (
                  <>
                    <div>
                      <p className="text-sm text-gray-500 mb-1 flex items-center gap-1"><Calendar size={14} /> Date Approved</p>
                      <p className="font-medium text-gray-900 text-sm">{new Date(details.date_approved).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1 flex items-center gap-1"><User size={14} /> Approved By</p>
                      <p className="font-medium text-gray-900 text-sm">{details.approved_by_name || "N/A"}</p>
                    </div>
                  </>
                )}

                <div>
                  <p className="text-sm text-gray-500 mb-1 flex items-center gap-1"><Calendar size={14} /> Date Supplied</p>
                  <p className="font-medium text-gray-900 text-sm">{details.date_supplied ? new Date(details.date_supplied).toLocaleString() : "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1 flex items-center gap-1"><User size={14} /> Supplied By</p>
                  <p className="font-medium text-gray-900 text-sm">{details.supplied_by_name || "N/A"}</p>
                </div>
              </div>

              {details.status === "Requested" && details.rejection_reason && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 mt-4">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-sm text-red-600 font-medium flex items-center gap-2">
                      <AlertCircle size={16} /> Rejection Reason
                    </p>
                    <div className="text-right">
                      <p className="text-xs text-red-500 font-medium mb-0.5">
                        Rejected By: {details.rejected_by_name || "N/A"}
                      </p>
                      {details.date_rejected && (
                        <p className="text-xs text-red-400">
                          Date: {new Date(details.date_rejected).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-red-800">{details.rejection_reason}</p>
                </div>
              )}

            </div>
          ) : (
            <div className="text-center py-10 text-gray-500">
              Failed to load details.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div>
            {details?.status === "Dispatched" && (
              <RoleBasedComponent
                allowedRoles={["warehouse_manager", "dispenser"]}
              >
                <div className="flex gap-2">
                  <button
                    onClick={() => onApprove(details.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm shadow-sm"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => onReject(details)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm shadow-sm"
                  >
                    Reject
                  </button>
                </div>
              </RoleBasedComponent>
            )}
          </div>
          <button
            onClick={() => setModalOpen(false)}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequisitionItemDetailsModal;
